import { PDFDocument, PageSizes, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import moment from 'moment';
import { currencyFormatter } from '@helpers/formatter';
import { saveBlob } from './download';

export const createCommissionDetailsReportPdf = async (data, storeConfig, t) => {
  const { currencySymbol, language } = storeConfig;
  const [
    orderTotal,
    commissionTotal,
    commissionTotalWithTax,
    totalCommissionTax,
  ] = data.data.reduce((acc, curr) => {
    acc[0] += parseInt(curr.purchases_with_taxes);
    acc[1] += curr.commission_amount_without_tax;
    acc[2] += curr.commission_amount_with_tax;
    acc[3] += curr.tax_amount_on_commission;
    return acc;
  }, [0, 0, 0, 0]);

  const pdfDoc = await PDFDocument.create()
  pdfDoc.registerFontkit(fontkit)

  let pageCount = 1;
  if (data.data.length > 21) {
    pageCount = 1 + Math.ceil((data.data.length - 21) /  25);
  }

  const fontUrls = [
    {
      url: language === 'ja' ? '/fonts/NotoSansJP-Regular.ttf' : '/fonts/ColgateReady-Light.otf',
      type: 'regular',
    },
    {
      url: language === 'ja' ? '/fonts/NotoSansJP-Bold.ttf' : '/fonts/ColgateReady-Bold.otf',
      type: 'bold',
    }
  ];
  const fontArray = await Promise.all(fontUrls.map(async (font) => {
    return {
      bytes: await fetch(font.url).then(res => res.arrayBuffer()),
      type: font.type,
    }
  }))
  const fonts = {};
  await Promise.all(fontArray.map(async (font) => {
    fonts[font.type] = await pdfDoc.embedFont(font.bytes)
    return font
  }))

  // logo
  const logoUrl = '/img/logo/MyHillsShop_logo_4C.png'
  const logoImageBytes = await fetch(logoUrl).then((res) => res.arrayBuffer())
  const logoImg = await pdfDoc.embedPng(logoImageBytes)

  
  for (let i = 1; i <= pageCount; i += 1) {
    const isFirstPage = i === 1;
    const isLastPage = i === pageCount;
    const page = pdfDoc.addPage(PageSizes.A4)
    const { width, height } = page.getSize()

    // header
    page.drawImage(logoImg, {
      ...pixelsToPoints({x: 77, y: 198}, height),
      width: 53,
      height: 53,
    })
    page.drawText(
      t('commissionDetailsReport_title'),
      {
        ...pixelsToPoints({x: 239, y: 112}, height),
        size: 16,
        font: fonts.bold,
        color: rgb(68/255, 70/255, 75/255),
      },
    )
    page.drawText(
      data.clinic_name,
      {
        ...pixelsToPoints({x: 1000, y: 112}, height),
        size: 11,
        color: rgb(68/255, 70/255, 75/255),
        font: fonts.bold,
      },
    )
    page.drawText(
      data.vet_name,
      {
        ...pixelsToPoints({x: 1000, y: 162}, height),
        size: 11,
        color: rgb(68/255, 70/255, 75/255),
        font: fonts.bold,
      },
    )
    page.drawText(
      data.startDate === 'Year to Date' ? 
       t('commissionDetailsReport_YTD') :
        `${t('commissionDetailsReport_From')} ${new Date(data.startDate).toLocaleDateString(language, {
          dateStyle: 'medium',
        })} ${t('commissionDetailsReport_to')} ${new Date(new Date(data.endDate).setDate(new Date(data.endDate).getDate()-1)).toLocaleDateString(language, {
          dateStyle: 'medium',
        })}`,
       
      {
        ...pixelsToPoints({x: 239, y: 155}, height),
        size: 9,
        color: rgb(68/255, 70/255, 75/255),
        font: fonts.regular
      },
    )
    page.drawRectangle({
      ...pixelsToPoints({x: 77, y: 253}, height),
      width: 523,
      height: 0,
      borderColor: rgb(198/255, 199/255, 204/255),
      borderWidth: 1,
    })
    // first page summary
    if (isFirstPage) {
      page.drawText(
        t('commissionDetailsReport_Commission_Total'),
        {
          ...pixelsToPoints({x: 660, y: 397}, height),
          size: 9,
          font: fonts.bold,
          color: rgb(68/255, 70/255, 75/255),
        }
      );

      page.drawText(
        t('commissionDetailsReport_Commission_With_Taxes'),
        {
          ...pixelsToPoints({x: 660, y: 458}, height),
          size: 11,
          font: fonts.regular,
          color: rgb(68/255, 70/255, 75/255),
        }
      );
      page.drawText(
        currencyFormatter.priceWithCurrency(
          commissionTotalWithTax,
          currencySymbol,
          language,
        ),
        {
          ...pixelsToPoints({x: 1000, y: 458}, height),
          size: 11,
          font: fonts.bold,
          color: rgb(68/255, 70/255, 75/255),
        }
      );

      page.drawText(
        t('commissionDetailsReport_Commission_Without_Taxes'),
        {
          ...pixelsToPoints({x: 660, y: 520}, height),
          size: 11,
          font: fonts.regular,
          color: rgb(68/255, 70/255, 75/255),
        }
      );
      page.drawText(
        currencyFormatter.priceWithCurrency(
          commissionTotal,
          currencySymbol,
          language,
        ),
        {
          ...pixelsToPoints({x: 1000, y: 520}, height),
          size: 11,
          font: fonts.bold,
          color: rgb(68/255, 70/255, 75/255),
        }
      );

      page.drawText(
        t('commissionDetailsReport_Tax_Amount_On_Commission'),
        {
          ...pixelsToPoints({x: 660, y: 580}, height),
          size: 11,
          font: fonts.regular,
          color: rgb(68/255, 70/255, 75/255),
        }
      );
      page.drawText(
        currencyFormatter.priceWithCurrency(
          totalCommissionTax,
          currencySymbol,
          language,
        ),
        {
          ...pixelsToPoints({x: 1000, y: 580}, height),
          size: 11,
          font: fonts.bold,
          color: rgb(68/255, 70/255, 75/255),
        }
      );
    }
    // table
    // header
    const pageOffset = isFirstPage ? 300 : 0;
    const headerCols = [
      {
        text: t('commissionDetailsReport_Fulfillment_Date'),
        x: 100,
        y: 420 + pageOffset,
      },
      {
        text: t('commissionDetailsReport_Order_Total_incl_Taxes'),
        x: 285,
        y: 420 + pageOffset,
      },
      {
        text: t('commissionDetailsReport_Commission_Rate'),
        x: 500,
        y: 420 + pageOffset,
      },
      {
        text: t('commissionDetailsReport_Commission_Total_PreTax'),
        x: 650,
        y: 420 + pageOffset,
      },
      {
        text: t('commissionDetailsReport_Commission_incl_Taxes'),
        x: 870,
        y: 420 + pageOffset,
      },
      {
        text: t('commissionDetailsReport_Total_Tax_Applied_Tn_Commission'),
        x: 1085,
        y: 420 + pageOffset,
      },
    ]
    headerCols.forEach((col) => {
      page.drawText(
        col.text,
        {
          ...pixelsToPoints({x: col.x, y: col.y}, height),
          size: 8,
          font: fonts.bold,
          width: 57,
          lineHeight: 9,
          color: rgb(68/255, 70/255, 75/255),
        }
      );
    })

    // table rows
    const itemsStart = isFirstPage ? 0 : 21 + (i - 2) * 25;
    const itemsEnd = isFirstPage ? 21 : 21 + (i - 1) * 25;
    data.data.slice(itemsStart, itemsEnd).forEach((row, index, arr) => {
      if (index % 2 === 0) {
        page.drawRectangle({
          ...pixelsToPoints({x: 77, y: 555 + (index * 73.7) + pageOffset}, height),
          width: 523,
          height: 22,
          color: rgb(251/255, 251/255, 251/255),
          borderWidth: 0,
        })
      }
      page.drawRectangle({
        ...pixelsToPoints({x: 77, y: 555 + (index * 73.7) + pageOffset}, height),
        width: 523,
        height: 1,
        color: rgb(198/255, 199/255, 204/255),
        borderWidth: 0,
      })

      const cols = [
        {
          text: new Date(row.order_fullfillment_date).toLocaleDateString(language, {
            dateStyle: 'medium',
          }),
          x: 100
        },
        {
          text: currencyFormatter.priceWithCurrency(
            parseInt(row.purchases_with_taxes),
            currencySymbol,
            language,
          ),
          x: 285
        },
        {
          text: `${row.commission_rate*100}%`,
          x: 500
        },
        {
          text: currencyFormatter.priceWithCurrency(
            row.commission_amount_without_tax,
            currencySymbol,
            language,
          ),
          x: 650
        },
        {
          text: currencyFormatter.priceWithCurrency(
            row.commission_amount_with_tax,
            currencySymbol,
            language,
          ),
          x: 870
        },
        {
          text: currencyFormatter.priceWithCurrency(
            row.tax_amount_on_commission,
            currencySymbol,
            language,
          ),
          x: 1085
        },
      ]
      cols.forEach((col) => {
        page.drawText(
          col.text,
          {
            ...pixelsToPoints({x: col.x, y: 530 + (index * 73.7) + pageOffset}, height),
            size: 8,
            width: 57,
            lineHeight: 9,
            color: rgb(68/255, 70/255, 75/255),
          }
        );
      });

      if (isLastPage && index === arr.length - 1) {
        const cols = [
          {
            text: t('commissionDetailsReport_Total'),
            x: 100
          },
          {
            text: currencyFormatter.priceWithCurrency(
              orderTotal,
              currencySymbol,
              language,
            ),
            x: 285
          },
          {
            text: t('commissionDetailsReport_NA'),
            x: 500
          },
          {
            text: currencyFormatter.priceWithCurrency(
              commissionTotal,
              currencySymbol,
              language,
            ),
            x: 650
          },
          {
            text: currencyFormatter.priceWithCurrency(
              commissionTotalWithTax,
              currencySymbol,
              language,
            ),
            x: 870
          },
          {
            text: currencyFormatter.priceWithCurrency(
              totalCommissionTax,
              currencySymbol,
              language,
            ),
            x: 1085
          },
        ];
        page.drawRectangle({
          ...pixelsToPoints({x: 77, y: 555 + ((index + 1) * 73.7) + pageOffset}, height),
          width: 523,
          height: 24,
          color: rgb(241/255, 246/255, 251/255),
          borderWidth: 0,
        })
        cols.forEach((col) => {
          page.drawText(
            col.text,
            {
              ...pixelsToPoints({x: col.x, y: 530 + ((index + 1) * 73.7) + pageOffset}, height),
              size: 8,
              width: 57,
              lineHeight: 9,
              color: rgb(68/255, 70/255, 75/255),
            }
          )
        });
      }
    })

    if (!isLastPage) {
      page.drawRectangle({
        ...pixelsToPoints({x: 77, y: 2345}, height),
        width: 523,
        height: 1,
        color: rgb(198/255, 199/255, 204/255),
        borderWidth: 0,
      })
      page.drawText(
        t('dashboard_Continue'),
        {
          ...pixelsToPoints({x: 650, y: 2380}, height),
          size: 8,
          font: fonts.bold,
          width: 57,
          lineHeight: 9,
          color: rgb(68/255, 70/255, 75/255),
        },
      )
    }

    // footer
    page.drawRectangle({
      ...pixelsToPoints({x: 77, y: 2400}, height),
      width: 523,
      height: 0,
      borderColor: rgb(198/255, 199/255, 204/255),
      borderWidth: 1,
    })
    page.drawText(
      t('commissionDetailsReport_Copyright'),
      {
        ...pixelsToPoints({x: 77, y: 2470}, height),
        size: 6,
        width: 57,
        lineHeight: 9,
        color: rgb(68/255, 70/255, 75/255),
      },
    )
    page.drawText(
      t('commissionDetailsReport_url'),
      {
        ...pixelsToPoints({x: 580, y: 2470}, height),
        size: 6,
        width: 57,
        lineHeight: 9,
        color: rgb(68/255, 70/255, 75/255),
      },
    )
    page.drawText(
      t('commissionDetailsReport_email'),
      {
        ...pixelsToPoints({x: pageCount === 1 ? 1080 : 850, y: 2470}, height),
        size: 6,
        width: 57,
        lineHeight: 9,
        color: rgb(68/255, 70/255, 75/255),
      },
    )
    page.drawText(
      `${t('commissionDetailsReport_page')} ${i} ${t('commissionDetailsReport_of')} ${pageCount}`,
      {
        ...pixelsToPoints({x: 1180, y: 2470}, height),
        size: 6,
        width: 57,
        lineHeight: 9,
        color: rgb(68/255, 70/255, 75/255),
      },
    )
  }

  const pdfBytes = await pdfDoc.save()
  saveBlob(
    `${data.clinic_name}_${
      data.endDate === 'Year to Date' ?
        'YTD' :
        `${moment.utc(data.startDate).format('YYYY/MM/DD')}/${moment.utc(data.endDate).subtract(1, 'days').format('YYYY/MM/DD')}`
      }.pdf`,
      new Blob([pdfBytes], {type: "application/pdf"})
  );
}
// figma page 1336 x 1891
// pdf page 595 x 842
// https://github.com/Hopding/pdf-lib/issues/19
// > the default value of 1⁄72 inch is used
// still not sure how I got this values but it somewhat works
// still not ideal tbh and needs adjustments to y
const pixelsToPoints = ({x, y}, height) => {
  return {
    x: x * 0.44,
    y: height - 28 - y * 0.32,
  }
};